/**
 * StatisticDaily
 * --
 */
import React from 'react';
import { Descriptions, Table, Button, Checkbox } from 'antd';
import { Content, Title, Row, Col } from 'components';
import moment from 'moment';
import MessageAlert, { stringToMoneyFormat } from 'utils';
// import { downloadXlsx } from 'utils/XlsxDownloader';
import * as XLSX from 'xlsx/xlsx.mjs';
/* 
사용중인 필드
  date: 날짜
  totalAmount: Total(합계금액+환불금액+할인금액)
  completedTotalAmount: 합계금액(결제완료건)
  netSales: 순매출(합계금액 - 할인금액)
  paymentsCount: 결제건수(결제완료건)
  discountAmount: 할인금액
  usePoint: 사용포인트
  couponAppliedCount: 쿠폰적용 주문수
  refundAmount: 환불금액
  refundCount: 환불건수
  totalAmountKIOSK: KIOSK합계금액
  paymentsCountKIOSK: KIOSK결제건수
  totalAmountAPP: APP합계금액
  paymentsCountAPP: APP결제건수
  totalAmountPOS: POS합계금액
  paymentsCountPOS: POS결제건수
*/

const CORRECTION_VALUE = 2;

/**----------------------------------------
 * [Component] 일자별 매출
 * ----------------------------------------
 */
const StatisticDaily = ({
  // shopList, // 업체목록
  // currentShop, // 선택된 업체 키
  data, // 차트 데이터
  startDate,
  endDate,
  onSelect,
  dateRange,
  // finalDiscount,
  summaryMode,
}) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */

  /* ===== VARIABLE ===== */
  // const currentShopInfo = shopList.filter(({ key }) => key === currentShop)[0];
  /* ===== FUNCTION ===== */
  /**
   * 엑셀 다운로드
   * --
   */
  const handleDownloadExel = () => {
    try {
      const xlsxData = data.map((item, idx) => {
        return {
          ['날짜']: moment(item.date).format('YYYY-MM-DD(ddd)'),
          ['Total(총매출+할인+포인트+환불)']:
            Number(item.totalAmount) + Number(item.refundAmount),
          ['총매출']: Number(item.totalAmount),
          ['순매출']: Number(item.netsAmount),
          ['총 결제건수']: Number(item.paymentCount ?? 0),
          ['할인']: Number(item.discountAmount ?? 0),
          ['사용포인트']: Number(item.usePointAmount ?? 0),
          ['환불금액']: Number(item.refundAmount ?? 0),
          ['환불건수']: Number(item.refundCount ?? 0),
          ['KIOSK총매출']: Number(item.kioskAmount ?? 0),
          ['KIOSK결제건수']: Number(item.kioskCount ?? 0),
          ['APP총매출']: Number(item.appAmount ?? 0),
          ['APP결제건수']: Number(item.appCount ?? 0),
          ['POS총매출']: Number(item.posAmount ?? 0),
          ['POS결제건수']: Number(item.posCount ?? 0),
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(
        wb,
        `일자별 매출_${moment().format('YYYYMMDDHHmmss')}.xlsx`,
      );
    } catch (e) {
      console.log(e);
      MessageAlert.error(e.message);
    }
  };

  /**
   *
   * --
   */
  const handleCalculate = (d, key) => {
    return d.reduce((acc, cur) => acc + Number(cur[key] ?? 0), 0);
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  const col = [
    {
      title: '날짜',
      dataIndex: 'date',
      width: 120,
      fixed: 'left',
      render: (v) => moment(v).format('YYYY-MM-DD(ddd)'),
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          Total
          <br />
          <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
            (총매출+할인+포인트+환불)
          </span>
        </p>
      ),
      dataIndex: 'total',
      width: 165,
      // align: 'center',
      render: (_, _record) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(
            Number(_record.totalAmount) + Number(_record.refundAmount),
          )}
        </div>
      ),
      sorter: (a, b) =>
        Number(a.totalAmount) +
        Number(a.refundAmount) -
        (Number(b.totalAmount) + Number(b.refundAmount)),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          순매출
          <br />
          <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
            (총매출-할인)
          </span>
        </p>
      ),
      dataIndex: 'netsAmount',
      width: 128,
      render: (v) => {
        return (
          <div style={{ textAlign: 'right' }}>
            {stringToMoneyFormat(Number(v))}
          </div>
        );
      },
      sorter: (a, b) => Number(a.netsAmount) - Number(b.netsAmount),
    },
    {
      title: '총매출',
      dataIndex: 'totalAmount',
      width: 120,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.totalAmount) - Number(b.totalAmount),
    },
    {
      title: '총 결제건수',
      dataIndex: 'paymentCount',
      width: 100,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.paymentCount) - Number(b.paymentCount),
    },
    {
      title: '할인',
      dataIndex: 'discountAmount',
      width: 100,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.discountAmount) - Number(b.discountAmount),
    },
    {
      title: '사용포인트',
      dataIndex: 'usePointAmount',
      width: 100,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.usePointAmount) - Number(b.usePointAmount),
    },
    {
      // title: '쿠폰',
      title: '쿠폰적용 주문수',
      dataIndex: 'couponAppliedCount',
      width: 125,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) =>
        Number(a.couponAppliedCount) - Number(b.couponAppliedCount),
    },
    {
      title: '환불금액',
      dataIndex: 'refundAmount',
      width: 125,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.refundAmount) - Number(b.refundAmount),
    },
    {
      title: '환불건수',
      dataIndex: 'refundCount',
      width: 90,
      render: (v) => (
        <div style={{ textAlign: 'right' }}>
          {stringToMoneyFormat(Number(v))}
        </div>
      ),
      sorter: (a, b) => Number(a.refundCount) - Number(b.refundCount),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          KIOSK
          <br />
          총매출<small>(환불)</small>
        </p>
      ),
      dataIndex: 'kioskAmount',
      width: 110,
      render: (v, record) => (
        <div style={{ textAlign: 'right', lineHeight: 1.1 }}>
          {stringToMoneyFormat(Number(v))}
          <br />
          <small>
            ({stringToMoneyFormat(Number(record.kioskAmountRefund))})
          </small>
        </div>
      ),
      sorter: (a, b) => Number(a.kioskAmount) - Number(b.kioskAmountRefund),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          KIOSK
          <br />
          결제건수<small>(환불)</small>
        </p>
      ),
      dataIndex: 'kioskCount',
      width: 120,
      render: (v, record) => (
        <div style={{ textAlign: 'right', lineHeight: 1.1 }}>
          {stringToMoneyFormat(Number(v))}
          <br />
          <small>
            ({stringToMoneyFormat(Number(record.kioskCountRefund))})
          </small>
        </div>
      ),
      sorter: (a, b) => Number(a.kioskCount) - Number(b.kioskCount),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          APP
          <br />
          총매출<small>(환불)</small>
        </p>
      ),
      dataIndex: 'appAmount',
      width: 110,
      render: (v, record) => (
        <div style={{ textAlign: 'right', lineHeight: 1.1 }}>
          {stringToMoneyFormat(Number(v))}
          <br />
          <small>({stringToMoneyFormat(Number(record.appAmountRefund))})</small>
        </div>
      ),
      sorter: (a, b) => Number(a.appAmount) - Number(b.appAmount),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          APP
          <br />
          결제건수<small>(환불)</small>
        </p>
      ),
      dataIndex: 'appCount',
      width: 120,
      render: (v, record) => (
        <div style={{ textAlign: 'right', lineHeight: 1.1 }}>
          {stringToMoneyFormat(Number(v))}
          <br />
          <small>({stringToMoneyFormat(Number(record.appAmountRefund))})</small>
        </div>
      ),
      sorter: (a, b) => Number(a.appCount) - Number(b.appCount),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          POS
          <br />
          총매출<small>(환불)</small>
        </p>
      ),
      dataIndex: 'posAmount',
      width: 110,
      render: (v, record) => (
        <div style={{ textAlign: 'right', lineHeight: 1.1 }}>
          {stringToMoneyFormat(Number(v))}
          <br />
          <small>({stringToMoneyFormat(Number(record.posAmountRefund))})</small>
        </div>
      ),
      sorter: (a, b) => Number(a.posAmount) - Number(b.posAmount),
    },
    {
      title: (
        <p style={{ lineHeight: 1.1 }}>
          POS
          <br />
          결제건수<small>(환불)</small>
        </p>
      ),
      dataIndex: 'posCount',
      width: 120,
      render: (v, record) => (
        <div style={{ textAlign: 'right', lineHeight: 1.1 }}>
          {stringToMoneyFormat(Number(v))}
          <br />
          <small>({stringToMoneyFormat(Number(record.posCountRefund))})</small>
        </div>
      ),
      sorter: (a, b) => Number(a.posCount) - Number(b.posCount),
    },
  ];

  const [columnsKey, setColumnsKey] = React.useState(
    col.map((c) => c.dataIndex),
  );

  const visibleColumns = React.useMemo(() => {
    return col.filter((c) => columnsKey.includes(c.dataIndex));
  }, [columnsKey]);

  const columnOptions = col.map((c) => {
    return {
      label:
        typeof c.title !== 'string'
          ? c?.title?.props.children
              .map((child) =>
                typeof child === 'string' ? child : child.props?.children || '',
              )
              .join(' ')
          : c.title,
      value: c.dataIndex,
    };
  });
  return (
    <>
      <Content maxWidth={'100%'} padding={0} backgroundColor={'none'}>
        <Col
          x={24}
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
            marginBottom: '5px',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Checkbox
            indeterminate={
              columnsKey.length > 0 && columnsKey.length < columnOptions.length
            }
            checked={columnsKey.length === columnOptions.length}
            onChange={(e) => {
              if (e.target.checked) {
                setColumnsKey(columnOptions.map((c) => c.value));
              } else {
                setColumnsKey([]);
              }
            }}
          >
            전체 선택
          </Checkbox>
          <Checkbox.Group
            options={columnOptions}
            value={columnsKey}
            onChange={(checkedValues) => setColumnsKey(checkedValues)}
          />
        </Col>

        <Row>
          {/* === 상단 > 타이틀 === */}
          <Col x={12} style={{ display: 'flex', gap: 5 }}>
            <Title
              style={{ marginBottom: 7.5 }}
              subButton={
                !summaryMode && (
                  <Button
                    style={{ marginLeft: 7.5 }}
                    onClick={() => handleDownloadExel()}
                  >
                    엑셀로 다운로드
                  </Button>
                )
              }
            >
              일자별 리포트
            </Title>
          </Col>
          <Col x={12} style={{ textAlign: 'right' }}>
            <Title
              level={5}
              align="right"
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                marginBottom: 7.5,
              }}
            >
              <span style={{ fontSize: '0.8em', fontWeight: '500' }}>
                조회기간:&nbsp;&nbsp;
                {!dateRange.startDate || !dateRange.endDate ? (
                  <>
                    {moment(startDate).format('YYYY.MM.DD(ddd)')}~
                    {moment(endDate).format('YYYY.MM.DD(ddd)')}
                  </>
                ) : (
                  <>
                    {moment(dateRange.startDate).format('YYYY.MM.DD(ddd)')}~
                    {moment(dateRange.endDate).format('YYYY.MM.DD(ddd)')}
                  </>
                )}
              </span>
            </Title>
          </Col>
        </Row>

        {/* ===== 데이터 Header: start ===== */}
        <div style={{ overflowX: 'auto', background: '#fff' }}>
          {data && (
            <Descriptions
              className="boxShadow"
              size="small"
              layout="vertical"
              column={16}
              bordered
              style={{ width: 1740 }}
              contentStyle={{ textAlign: 'right' }}
              items={[
                {
                  key: 'date',
                  label: '',
                  style: { width: 120 + CORRECTION_VALUE },
                  contentStyle: { textAlign: 'center' },
                  children: <p>합계</p>,
                },
                {
                  key: 'totalAmount',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      Total
                      <br />
                      <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
                        (총매출+할인+포인트+환불)
                      </span>
                    </p>
                  ),
                  style: { width: 175 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'netsAmount') +
                          Math.abs(handleCalculate(data, 'discountAmount')) +
                          Math.abs(handleCalculate(data, 'usePointAmount')) +
                          handleCalculate(data, 'refundAmount'),
                      )}
                    </p>
                  ),
                },
                {
                  key: 'netSales',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      순매출
                      <br />
                      <span style={{ fontSize: '0.8em', color: '#7f7f7f' }}>
                        (총매출-할인)
                      </span>
                    </p>
                  ),
                  style: { width: 130 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(handleCalculate(data, 'netsAmount'))}
                    </p>
                  ),
                },
                {
                  key: 'completedTotalAmount',
                  label: '총매출',
                  style: { width: 120 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'totalAmount') ??
                          0 + handleCalculate(data, 'refundAmount') ??
                          0,
                      )}
                    </p>
                  ),
                },

                {
                  key: 'paymentsCount',
                  label: '총 결제건수',
                  style: { width: 110 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'paymentCount'),
                      )}
                    </p>
                  ),
                },
                {
                  key: 'discountAmount',
                  label: '할인',
                  style: { width: 100 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'discountAmount'),
                      )}
                    </p>
                  ),
                },
                {
                  key: 'usePointAmount',
                  label: '사용포인트',
                  style: { width: 100 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'usePointAmount'),
                      )}
                    </p>
                  ),
                },
                {
                  key: 'couponAppliedCount',
                  // label: '쿠폰',
                  label: '쿠폰적용 주문',
                  style: { width: 125 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'couponAppliedCount'),
                      )}
                    </p>
                  ),
                },
                {
                  key: 'refundAmount',
                  label: '환불금액',
                  style: { width: 125 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'refundAmount'),
                      )}
                    </p>
                  ),
                },
                {
                  key: 'refundCount',
                  label: '환불건수',
                  style: { width: 90 + CORRECTION_VALUE },
                  children: (
                    <p>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'refundCount'),
                      )}
                    </p>
                  ),
                },
                {
                  key: 'kioskAmount',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      KIOSK
                      <br />
                      총매출<small>(환불)</small>
                    </p>
                  ),
                  style: { width: 110 + CORRECTION_VALUE },
                  children: (
                    <p style={{ lineHeight: 1.1 }}>
                      {stringToMoneyFormat(
                        handleCalculate(data, 'kioskAmount'),
                      )}
                      <br />
                      <small>
                        (
                        {stringToMoneyFormat(
                          handleCalculate(data, 'kioskAmountRefund'),
                        )}
                        )
                      </small>
                    </p>
                  ),
                },
                {
                  key: 'kioskCount',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      KIOSK
                      <br />
                      결제건수<small>(환불)</small>
                    </p>
                  ),
                  style: { width: 110 + CORRECTION_VALUE },
                  children: (
                    <p style={{ lineHeight: 1.1 }}>
                      {stringToMoneyFormat(handleCalculate(data, 'kioskCount'))}
                      <br />
                      <small>
                        (
                        {stringToMoneyFormat(
                          handleCalculate(data, 'kioskCountRefund'),
                        )}
                        )
                      </small>
                    </p>
                  ),
                },
                {
                  key: 'appAmount',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      APP
                      <br />
                      총매출<small>(환불)</small>
                    </p>
                  ),
                  style: { width: 110 + CORRECTION_VALUE },
                  children: (
                    <p style={{ lineHeight: 1.1 }}>
                      {stringToMoneyFormat(handleCalculate(data, 'appAmount'))}
                      <br />
                      <small>
                        (
                        {stringToMoneyFormat(
                          handleCalculate(data, 'appAmountRefund'),
                        )}
                        )
                      </small>
                    </p>
                  ),
                },
                {
                  key: 'appCount',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      APP
                      <br />
                      결제건수<small>(환불)</small>
                    </p>
                  ),
                  style: { width: 110 + CORRECTION_VALUE },
                  children: (
                    <p style={{ lineHeight: 1.1 }}>
                      {stringToMoneyFormat(handleCalculate(data, 'appCount'))}
                      <br />
                      <small>
                        (
                        {stringToMoneyFormat(
                          handleCalculate(data, 'appCountRefund'),
                        )}
                        )
                      </small>
                    </p>
                  ),
                },
                {
                  key: 'posAmount',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      POS
                      <br />
                      총매출<small>(환불)</small>
                    </p>
                  ),
                  style: { width: 110 + CORRECTION_VALUE },
                  children: (
                    <p style={{ lineHeight: 1.1 }}>
                      {stringToMoneyFormat(handleCalculate(data, 'posAmount'))}
                      <br />
                      <small>
                        (
                        {stringToMoneyFormat(
                          handleCalculate(data, 'posAmountRefund'),
                        )}
                        )
                      </small>
                    </p>
                  ),
                },
                {
                  key: 'posCount',
                  label: (
                    <p style={{ lineHeight: 1.1 }}>
                      POS
                      <br />
                      결제건수<small>(환불)</small>
                    </p>
                  ),
                  style: { width: 110 + CORRECTION_VALUE },
                  children: (
                    <p style={{ lineHeight: 1.1 }}>
                      {stringToMoneyFormat(handleCalculate(data, 'posCount'))}
                      <br />
                      <small>
                        (
                        {stringToMoneyFormat(
                          handleCalculate(data, 'posCountRefund'),
                        )}
                        )
                      </small>
                    </p>
                  ),
                },
              ]}
            />
          )}
        </div>
        {/* ===== 데이터 Header: end ===== */}
        <br />

        {/* ===== 데이터 테이블: start ===== */}

        {!summaryMode && (
          <Table
            className="boxShadow"
            bordered
            size="small"
            dataSource={data ?? []}
            scroll={{
              x: 'max-content',
            }}
            style={{
              minHeight: 580,
            }}
            onRow={(record) => {
              return {
                onClick: () => onSelect(record), // 행 클릭 시 이벤트 처리
              };
            }}
            columns={visibleColumns}
          />
        )}
        {/* ===== 데이터 테이블: end ===== */}
      </Content>
    </>
  );
};

StatisticDaily.defaultProps = {
  onSelect: () => {},
};
export default StatisticDaily;
